import http from "./http.service";
import { history } from "../../_helpers";

/**
 * Las notificaciones push del navegador.
 *
 * <p>Dos cosas que parecen una sola y no lo son: el <b>permiso</b> lo da el usuario al navegador y
 * el <b>token</b> lo emite Firebase para este navegador en particular. Sin permiso no hay token, y
 * el token cambia solo —al limpiar los datos del sitio, al reinstalar el service worker o cuando el
 * propio FCM lo rota—, así que se reenvía al backend en cada arranque de sesión en vez de guardarlo
 * como si fuera un dato estable del usuario.
 */

// Tiene que coincidir EXACTAMENTE con public/firebase-messaging-sw.js: el token se emite contra
// este proyecto y el service worker escucha en el suyo. Si se separan, el push no llega y no hay
// ningún error que lo delate.
const firebaseConfig = {
    apiKey: "AIzaSyBD-2naBcaY2G26aCcuilZRFKg7sHHIYaY",
    authDomain: "deautopartes-e87b8.firebaseapp.com",
    projectId: "deautopartes-e87b8",
    storageBucket: "deautopartes-e87b8.firebasestorage.app",
    messagingSenderId: "568635624497",
    appId: "1:568635624497:web:1eceffb45bf0f122f59910",
    measurementId: "G-LD0JMWRZ3N",
};

/** Clave pública VAPID del proyecto (Firebase Console → Cloud Messaging → Web configuration). */
const VAPID_KEY =
    "BHb9v5V2_yKyGgWoenqJAHQBSiQJ7zJdQY3owrIBPCwro05tjqBZ7GczlA5vou_tmyEkvq0YZ2ZfvUla0wLTGoI";

/** Scope propio del SW de Firebase, separado del de la app. */
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";

class PushService {

    private escuchandoDeepLinks = false;

    /** Si el navegador puede recibir push. Safari viejo y los modos privados dicen que no. */
    get isSupported(): boolean {
        return (
            typeof window !== "undefined" &&
            "Notification" in window &&
            "serviceWorker" in navigator &&
            "PushManager" in window &&
            !this.iosSinInstalar
        );
    }

    /**
     * iPhone/iPad con el sitio abierto en Safari, sin agregar a la pantalla de inicio.
     *
     * <p>Es el único caso en que "no se puede" tiene arreglo del lado del usuario, así que se
     * distingue del resto: Safari expone `Notification` y `PushManager` igual, pero
     * `requestPermission()` devuelve `denied` sin preguntar nada mientras la web no corra como app
     * instalada. Sin separarlo, un usuario de iPhone ve el botón, lo toca, no pasa nada y queda
     * bloqueado para siempre.
     */
    get iosSinInstalar(): boolean {
        if (typeof window === "undefined" || typeof navigator === "undefined") return false;

        const ua = navigator.userAgent || "";
        // iPadOS 13+ miente y se declara Mac: se lo delata por ser táctil.
        const esIOS =
            /iPad|iPhone|iPod/.test(ua) ||
            (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
        if (!esIOS) return false;

        const instalada =
            (window.navigator as any).standalone === true ||
            (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);

        return !instalada;
    }

    /** Todavía no dijo ni que sí ni que no: es el único momento en que tiene sentido preguntarle. */
    get sePuedePreguntar(): boolean {
        return this.isSupported && this.permission === "default";
    }

    get permission(): NotificationPermission {
        return this.isSupported ? Notification.permission : "denied";
    }

    get isGranted(): boolean {
        return this.permission === "granted";
    }

    get isDenied(): boolean {
        return this.permission === "denied";
    }

    /**
     * Se llama al arrancar la sesión. Si el permiso ya está dado, refresca el token en silencio;
     * si nunca se pidió, no molesta: pedirlo sin que el usuario lo haya buscado es la forma más
     * rápida de que lo bloquee para siempre.
     */
    initialize = async (): Promise<void> => {
        this.escucharDeepLinks();
        // Un admin impersonando no registra su navegador: pisaria el dispositivo real del usuario (el
        // back tambien lo frena). Se lee la marca directo porque authService importa este servicio.
        if (localStorage.getItem("impersonando")) return;
        if (this.isGranted) {
            await this.registrarToken();
        }
    };

    /**
     * Pide el permiso y registra el dispositivo. Es lo que corre detrás del botón "Activar
     * notificaciones".
     *
     * <p>Tiene que llamarse SIEMPRE desde el click del usuario, sin un `await` de por medio: Safari
     * y los Chrome de Android descartan un `requestPermission()` que no venga de un gesto.
     *
     * @returns `blocked` cuando el usuario ya lo rechazó —desde el código no se puede volver a
     *          preguntar, hay que decirle que lo habilite desde el candado de la barra— y `error`
     *          cuando dio el permiso pero Firebase no emitió el token, que es el caso que hay que
     *          mostrar distinto porque no lo arregla el usuario.
     */
    activar = async (): Promise<{ success: boolean; blocked: boolean; error: boolean }> => {
        if (!this.isSupported) return { success: false, blocked: false, error: false };

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
            return { success: false, blocked: permission === "denied", error: false };
        }

        const token = await this.registrarToken();
        return { success: !!token, blocked: false, error: !token };
    };

    /** Deja de recibir push en este dispositivo: borra el token del backend y el local. */
    desactivar = async (): Promise<void> => {
        try {
            const { getMessaging, deleteToken } = await import("firebase/messaging");
            const app = await this.firebaseApp();
            await deleteToken(getMessaging(app));
        } catch {
            /* no había token, o el navegador no lo soporta */
        }
        await this.guardarToken("");
    };

    /** Emite el token contra el SW de Firebase y se lo manda al backend. */
    private registrarToken = async (): Promise<string | null> => {
        if (!this.isSupported) return null;

        try {
            const { getMessaging, getToken, onMessage } = await import("firebase/messaging");
            const app = await this.firebaseApp();
            const messaging = getMessaging(app);

            // El SW se registra EXPLÍCITAMENTE en su propio scope. Sin esto, si algún día vuelve a
            // registrarse el service worker de Workbox en '/', la suscripción push quedaría ambigua
            // entre los dos y la entrega se vuelve inconsistente.
            const swRegistration = await navigator.serviceWorker.register(
                "/firebase-messaging-sw.js",
                { scope: SW_SCOPE }
            );

            const token = await getToken(messaging, {
                vapidKey: VAPID_KEY,
                serviceWorkerRegistration: swRegistration,
            });

            if (!token) return null;

            await this.guardarToken(token);

            // Con la pestaña en primer plano el SW no se entera: FCM entrega acá. No se muestra una
            // tarjeta del sistema —sería redundante con la campana, que ya se movió por WebSocket—,
            // sólo se avisa al resto de la app.
            onMessage(messaging, (payload: any) => {
                window.dispatchEvent(
                    new CustomEvent("push-en-primer-plano", { detail: payload?.data || {} })
                );
            });

            return token;
        } catch (e: any) {
            // El modo de falla caro es silencioso: con el permiso dado y la VAPID equivocada, FCM
            // tira `messaging/token-subscribe-failed` y desde afuera se ve igual que "anda pero no
            // llega nada". Por eso el código de error va en el mensaje.
            console.error(
                "No se pudo registrar el dispositivo para notificaciones" +
                    (e?.code ? ` (${e.code})` : "") +
                    ". Si es token-subscribe-failed, revisar que VAPID_KEY sea la de Firebase " +
                    "Console -> Cloud Messaging -> Web Push certificates.",
                e
            );
            return null;
        }
    };

    private firebaseApp = async () => {
        const { initializeApp, getApps, getApp } = await import("firebase/app");
        return getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
    };

    private guardarToken = (fcmToken: string) =>
        http
            .post("/api/notificaciones/fcm-token", { fcmToken })
            .catch(() => undefined);

    /**
     * Navega cuando el usuario toca un push y la app ya estaba abierta.
     *
     * <p>La navegación la hace la app y no el service worker porque `WindowClient.navigate()` sólo
     * vale sobre páginas que ESE service worker controla, y las de la app no lo están: la llamada
     * falla en silencio y la ventana se queda donde estaba. El SW manda el destino por postMessage
     * y acá se contesta el acuse para que no intente el respaldo.
     */
    private escucharDeepLinks = () => {
        if (this.escuchandoDeepLinks || !("serviceWorker" in navigator)) return;
        this.escuchandoDeepLinks = true;

        navigator.serviceWorker.addEventListener("message", (ev: MessageEvent) => {
            const data: any = ev.data;
            if (!data || data.type !== "DEEP_LINK" || typeof data.url !== "string") return;

            // El acuse primero: si la navegación falla, el SW igual tiene que saber que alguien lo
            // escuchó y no duplicar el intento con un navigate().
            if (ev.ports && ev.ports[0]) ev.ports[0].postMessage({ ok: true });

            history.push(data.url);
        });
    };
}

const pushService = new PushService();

export default pushService;
